import React, { memo, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { UiButton } from '@shared/ui';

import cls from './ConnectionArrow.module.css';
import { queueFetch } from '../../../helpers/queueFetch';
import { removeConnection } from '../../../store/reducers/positionsConnections/actions/deleteConnection';
import { positionSelector } from '../../../store/reducers/positionSlice/positionSlice';
import Modal from '../Modal/Modal';
import { ModalBody } from '../Modal/ModalBody/ModalBody';
import { ModalFooter } from '../Modal/ModalFooter/ModalFooter';

export const ConnectionArrow = memo(({ positionX, connection, factLeft = 0 }) => {
    const { t } = useTranslation();
    const positionTo = useSelector((state) => positionSelector.selectById(state, connection.to));
    const dispatch = useDispatch();
    const { type, from, to } = connection;
    const { activeTab, opensStages, opensCharters } = useSelector((state) => state.gantReducer);
    const [isHover, setIsHover] = useState(false);
    const [isShowModal, setIsShowModal] = useState(false);
    const [isShowArrow, setIsShowArrow] = useState(false);
    const [arrowData, setArrowData] = useState(null);
    const computeArrowData = useCallback(() => {
        const fromEl = document.querySelector(`[data-position-guid="${from}"]`);
        const toEl = document.querySelector(`[data-position-guid="${to}"]`);
        if (!fromEl || !toEl) return null;

        const fromRect = fromEl.getBoundingClientRect();
        const toRect = toEl.getBoundingClientRect();

        const distance = Math.abs(toRect.left - fromRect.left);
        const height = toRect.top - fromRect.top;
        const fromPositionWidth = fromRect.width;
        const toPositionWidth = toRect.width;

        let scaleError = 0;
        if (activeTab === 'week') {
            if (toPositionWidth < 12) {
                scaleError = 4;
            }
        } else if (activeTab === 'month') {
            if (toPositionWidth < 8) {
                scaleError = 6;
            } else if (toPositionWidth < 15) {
                scaleError = 4;
            }
        }
        const arrowWidth =
            type === 1
                ? distance - toPositionWidth - scaleError
                : distance - fromPositionWidth - scaleError;

        return { height, fromPositionWidth, toPositionWidth, arrowWidth };
    }, [activeTab, from, to, type]);

    useEffect(() => {
        const data = computeArrowData();

        if (data) {
            setArrowData(data);
            setIsShowArrow(true);
        } else {
            setIsShowArrow(false);
        }
    }, [activeTab, positionTo, opensStages, opensCharters]);

    if (!isShowArrow) return null;

    const deleteConnection = () => {
        const queueConnections = [
            {
                data: {
                    data: {
                        connection: [
                            {
                                positionFromGuid: connection.from,
                                positionToGuid: connection.to,
                            },
                        ],
                    },
                    type: 'remove_connections',
                    action: 'remove_connections',
                },
                subEntityType: 'step',
                t: Date.now(),
            },
        ];

        queueFetch(queueConnections);
        dispatch(removeConnection(connection));
        setIsShowModal(false);
    };

    const onMouseEnter = () => {
        if (isPlanFact) return;
        setIsHover(true);
    };
    const onMouseLeave = () => {
        if (isPlanFact) return;
        setIsHover(false);
    };

    return (
        <>
            <Modal isOpen={isShowModal} onHideModal={() => setIsShowModal(false)}>
                <ModalBody>{t('Gant.ConnectionArrow.return.Modal.ModalBody')}</ModalBody>
                <ModalFooter>
                    <div className={classNames(cls.buttonWrapper)}>
                        <UiButton
                            btnColor="danger"
                            btnText={t('button.delete.text')}
                            onClick={deleteConnection}
                        />
                        <UiButton
                            btnColor="secondary"
                            btnText={t('button.cancel.text')}
                            onClick={() => setIsShowModal(false)}
                        />
                    </div>
                </ModalFooter>
            </Modal>

            {+type === 0 && arrowData.height > 0 && (
                <div
                    className={classNames(cls.wrapper, { [cls.activeWrapper]: isHover })}
                    style={{ left: arrowData.fromPositionWidth }}
                >
                    {isHover && (
                        <div
                            className={cls.delete}
                            style={{
                                left: arrowData.arrowWidth + 2,
                                top: isPlanFact ? 8 : '',
                            }}
                            onClick={() => setIsShowModal(true)}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_1561_40383)">
                                    <rect width="16" height="16" rx="8" fill="white" />
                                    <path
                                        d="M5.12 12L8 9.12L10.88 12L12 10.88L9.12 8L12 5.12L10.88 4L8 6.88L5.12 4L4 5.12L6.88 8L4 10.88L5.12 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16ZM8 14.4C9.78667 14.4 11.3 13.78 12.54 12.54C13.78 11.3 14.4 9.78667 14.4 8C14.4 6.21333 13.78 4.7 12.54 3.46C11.3 2.22 9.78667 1.6 8 1.6C6.21333 1.6 4.7 2.22 3.46 3.46C2.22 4.7 1.6 6.21333 1.6 8C1.6 9.78667 2.22 11.3 3.46 12.54C4.7 13.78 6.21333 14.4 8 14.4Z"
                                        fill="#1094F7"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_1561_40383">
                                        <rect width="16" height="16" rx="8" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>
                    )}

                    <div
                        style={{
                            width: arrowData.arrowWidth + 10 - positionX,
                            left: positionX + factLeft,
                            height: 15,
                            top: isPlanFact ? 0 : '',
                        }}
                        className={classNames(cls.lineArrowWrapper)}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    >
                        <div
                            style={{
                                height: 2,
                                width: arrowData.arrowWidth + 10 - positionX,
                                top: isPlanFact ? 0 : '',
                            }}
                            className={classNames(cls.lineArrow, { [cls.hovered]: isHover })}
                        />
                    </div>
                    <div
                        className={classNames(cls.lineArrowWrapper)}
                        style={{
                            height: arrowData.height - 10,
                            width: 15,
                            left: arrowData.arrowWidth + 2 + factLeft,
                            top: isPlanFact ? 7 : -1,
                        }}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    >
                        <div
                            style={{ height: arrowData.height - 10, width: 2 }}
                            className={classNames(cls.lineArrow, { [cls.hovered]: isHover })}
                        >
                            <svg
                                width="12"
                                height="8"
                                viewBox="0 0 12 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={cls.arrow}
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M0.292847 1.70718L1.70706 0.292969L5.99995 4.58586L10.2928 0.292969L11.7071 1.70718L5.99995 7.41429L0.292847 1.70718Z"
                                    fill="#1094F7"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            )}

            {+type === 0 && arrowData.height < 0 && (
                <div
                    className={classNames(cls.wrapper, { [cls.activeWrapper]: isHover })}
                    style={{ left: arrowData.fromPositionWidth }}
                >
                    {isHover && (
                        <div
                            className={cls.delete}
                            style={{
                                left: arrowData.arrowWidth + 2,
                                top: isPlanFact ? 8 : '',
                            }}
                            onClick={() => setIsShowModal(true)}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_1561_40383)">
                                    <rect width="16" height="16" rx="8" fill="white" />
                                    <path
                                        d="M5.12 12L8 9.12L10.88 12L12 10.88L9.12 8L12 5.12L10.88 4L8 6.88L5.12 4L4 5.12L6.88 8L4 10.88L5.12 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16ZM8 14.4C9.78667 14.4 11.3 13.78 12.54 12.54C13.78 11.3 14.4 9.78667 14.4 8C14.4 6.21333 13.78 4.7 12.54 3.46C11.3 2.22 9.78667 1.6 8 1.6C6.21333 1.6 4.7 2.22 3.46 3.46C2.22 4.7 1.6 6.21333 1.6 8C1.6 9.78667 2.22 11.3 3.46 12.54C4.7 13.78 6.21333 14.4 8 14.4Z"
                                        fill="#1094F7"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_1561_40383">
                                        <rect width="16" height="16" rx="8" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>
                    )}
                    <div
                        style={{
                            width: arrowData.arrowWidth + 10 - positionX,
                            left: positionX + factLeft,
                            height: 15,
                            top: isPlanFact ? 0 : '',
                        }}
                        className={classNames(cls.lineArrowWrapper)}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    >
                        <div
                            style={{
                                height: 2,
                                width: arrowData.arrowWidth + 10 - positionX,
                                top: isPlanFact ? 0 : '',
                            }}
                            className={classNames(cls.lineArrow, { [cls.hovered]: isHover })}
                        />
                    </div>
                    <div
                        className={classNames(cls.lineArrowWrapper)}
                        style={{
                            height: Math.abs(arrowData.height) - 10,
                            width: 15,
                            left: arrowData.arrowWidth + 2 + factLeft,
                            top: isPlanFact ? arrowData.height + 19 : arrowData.height + 11,
                        }}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    >
                        <div
                            style={{ height: Math.abs(arrowData.height) - 10, width: 2 }}
                            className={classNames(cls.lineArrow, { [cls.hovered]: isHover })}
                        >
                            <svg
                                width="12"
                                height="8"
                                viewBox="0 0 12 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={cls.arrowDown}
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.7072 6.29282L10.2929 7.70703L6.00005 3.41414L1.70715 7.70703L0.29294 6.29282L6.00005 0.585711L11.7072 6.29282Z"
                                    fill="#1094F7"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            )}
            {+type === 1 && arrowData.height > 0 && (
                <div
                    className={classNames(cls.wrapper, { [cls.activeWrapper]: isHover })}
                    style={{ left: 0 }}
                >
                    {isHover && (
                        <div
                            className={cls.delete}
                            style={{
                                right: arrowData.arrowWidth + 1,
                                top: isPlanFact ? 8 : '',
                            }}
                            onClick={() => setIsShowModal(true)}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_1561_40383)">
                                    <rect width="16" height="16" rx="8" fill="white" />
                                    <path
                                        d="M5.12 12L8 9.12L10.88 12L12 10.88L9.12 8L12 5.12L10.88 4L8 6.88L5.12 4L4 5.12L6.88 8L4 10.88L5.12 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16ZM8 14.4C9.78667 14.4 11.3 13.78 12.54 12.54C13.78 11.3 14.4 9.78667 14.4 8C14.4 6.21333 13.78 4.7 12.54 3.46C11.3 2.22 9.78667 1.6 8 1.6C6.21333 1.6 4.7 2.22 3.46 3.46C2.22 4.7 1.6 6.21333 1.6 8C1.6 9.78667 2.22 11.3 3.46 12.54C4.7 13.78 6.21333 14.4 8 14.4Z"
                                        fill="#1094F7"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_1561_40383">
                                        <rect width="16" height="16" rx="8" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>
                    )}
                    <div
                        style={{
                            width: arrowData.arrowWidth + 10 + positionX,
                            right: -positionX - factLeft,
                            height: 15,
                            top: isPlanFact ? 0 : '',
                        }}
                        className={classNames(cls.lineArrowWrapper)}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    >
                        <div
                            style={{
                                height: 2,
                                width: arrowData.arrowWidth + 10 + positionX,
                                top: isPlanFact ? 0 : '',
                            }}
                            className={classNames(cls.lineArrow, { [cls.hovered]: isHover })}
                        />
                    </div>
                    <div
                        className={classNames(cls.lineArrowWrapper)}
                        style={{
                            height: -arrowData.height - 10,
                            width: 15,
                            right: arrowData.arrowWidth + 2 - factLeft,
                            top: isPlanFact ? 7 : -1,
                        }}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    >
                        <div
                            style={{ height: arrowData.height - 10, width: 2 }}
                            className={classNames(cls.lineArrow, { [cls.hovered]: isHover })}
                        >
                            <svg
                                width="12"
                                height="8"
                                viewBox="0 0 12 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={cls.arrow}
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M0.292847 1.70718L1.70706 0.292969L5.99995 4.58586L10.2928 0.292969L11.7071 1.70718L5.99995 7.41429L0.292847 1.70718Z"
                                    fill="#1094F7"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            )}
            {+type === 1 && arrowData.height < 0 && (
                <div
                    className={classNames(cls.wrapper, { [cls.activeWrapper]: isHover })}
                    style={{ left: 0 }}
                >
                    {isHover && (
                        <div
                            className={cls.delete}
                            style={{
                                right: arrowData.arrowWidth + 1,
                                top: isPlanFact ? 8 : '',
                            }}
                            onClick={() => setIsShowModal(true)}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_1561_40383)">
                                    <rect width="16" height="16" rx="8" fill="white" />
                                    <path
                                        d="M5.12 12L8 9.12L10.88 12L12 10.88L9.12 8L12 5.12L10.88 4L8 6.88L5.12 4L4 5.12L6.88 8L4 10.88L5.12 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16ZM8 14.4C9.78667 14.4 11.3 13.78 12.54 12.54C13.78 11.3 14.4 9.78667 14.4 8C14.4 6.21333 13.78 4.7 12.54 3.46C11.3 2.22 9.78667 1.6 8 1.6C6.21333 1.6 4.7 2.22 3.46 3.46C2.22 4.7 1.6 6.21333 1.6 8C1.6 9.78667 2.22 11.3 3.46 12.54C4.7 13.78 6.21333 14.4 8 14.4Z"
                                        fill="#1094F7"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_1561_40383">
                                        <rect width="16" height="16" rx="8" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>
                    )}
                    <div
                        style={{
                            width: arrowData.arrowWidth + 10 + positionX,
                            right: -positionX - factLeft,
                            height: 15,
                            top: isPlanFact ? 0 : '-7px',
                        }}
                        className={classNames(cls.lineArrowWrapper)}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    >
                        <div
                            style={{
                                height: 2,
                                width: arrowData.arrowWidth + 10 + positionX,
                                top: isPlanFact ? 0 : '',
                            }}
                            className={classNames(cls.lineArrow, { [cls.hovered]: isHover })}
                        />
                    </div>
                    <div
                        className={classNames(cls.lineArrowWrapper)}
                        style={{
                            height: Math.abs(arrowData.height) - 10,
                            width: 15,
                            right: arrowData.arrowWidth + 2 - factLeft,
                            top: isPlanFact ? arrowData.height + 19 : arrowData.height + 11,
                        }}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    >
                        <div
                            style={{ height: Math.abs(arrowData.height) - 10, width: 2 }}
                            className={classNames(cls.lineArrow, { [cls.hovered]: isHover })}
                        >
                            <svg
                                width="12"
                                height="8"
                                viewBox="0 0 12 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={cls.arrowDown}
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.7072 6.29282L10.2929 7.70703L6.00005 3.41414L1.70715 7.70703L0.29294 6.29282L6.00005 0.585711L11.7072 6.29282Z"
                                    fill="#1094F7"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});
